/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetBackTornateElettoraliParams,
  TornataElettorale,
  TornataElettoraleRequest,
  WsRispostaPaginataListTornataElettorale,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * API per la ricerca del dettaglio di una tornata elettorale
 */
export const getBackTornateElettoraliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TornataElettorale>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliIdQueryKey = (id: string) =>
  [`/framework-bo-procedimenti/tornate-elettorali/${id}`] as const;

export const getGetBackTornateElettoraliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettoraliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackTornateElettoraliIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettoraliId>>
  > = ({ signal }) => getBackTornateElettoraliId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetBackTornateElettoraliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettoraliId>>
>;
export type GetBackTornateElettoraliIdQueryError = ErrorType<unknown>;

export const useGetBackTornateElettoraliId = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per aggiornare una tornata elettorale
 */
export const putBackTornateElettoraliId = (
  id: string,
  tornataElettoraleRequest: TornataElettoraleRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TornataElettorale>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tornataElettoraleRequest,
    },
    options,
  );
};

export const getPutBackTornateElettoraliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackTornateElettoraliId>>,
    TError,
    { id: string; data: TornataElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackTornateElettoraliId>>,
  TError,
  { id: string; data: TornataElettoraleRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackTornateElettoraliId>>,
    { id: string; data: TornataElettoraleRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putBackTornateElettoraliId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackTornateElettoraliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackTornateElettoraliId>>
>;
export type PutBackTornateElettoraliIdMutationBody = TornataElettoraleRequest;
export type PutBackTornateElettoraliIdMutationError = ErrorType<unknown>;

export const usePutBackTornateElettoraliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackTornateElettoraliId>>,
    TError,
    { id: string; data: TornataElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackTornateElettoraliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per eliminare una tornata elettorale
 */
export const deleteBackTornateElettoraliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TornataElettorale>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackTornateElettoraliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackTornateElettoraliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackTornateElettoraliId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackTornateElettoraliId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteBackTornateElettoraliId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackTornateElettoraliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackTornateElettoraliId>>
>;

export type DeleteBackTornateElettoraliIdMutationError = ErrorType<unknown>;

export const useDeleteBackTornateElettoraliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackTornateElettoraliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackTornateElettoraliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per la ricerca paginata delle tornate elettorali, con la possibilità di filtrare i dati
 */
export const getBackTornateElettorali = (
  params?: GetBackTornateElettoraliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTornataElettorale>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliQueryKey = (
  params?: GetBackTornateElettoraliParams,
) =>
  [
    `/framework-bo-procedimenti/tornate-elettorali`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackTornateElettoraliQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettorali>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackTornateElettoraliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettorali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettorali>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackTornateElettoraliQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettorali>>
  > = ({ signal }) => getBackTornateElettorali(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackTornateElettoraliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettorali>>
>;
export type GetBackTornateElettoraliQueryError = ErrorType<unknown>;

export const useGetBackTornateElettorali = <
  TData = Awaited<ReturnType<typeof getBackTornateElettorali>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackTornateElettoraliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettorali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per creare una nuova tornata elettorale
 */
export const postBackTornateElettorali = (
  tornataElettoraleRequest: TornataElettoraleRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TornataElettorale>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tornataElettoraleRequest,
    },
    options,
  );
};

export const getPostBackTornateElettoraliMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackTornateElettorali>>,
    TError,
    { data: TornataElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackTornateElettorali>>,
  TError,
  { data: TornataElettoraleRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackTornateElettorali>>,
    { data: TornataElettoraleRequest }
  > = props => {
    const { data } = props ?? {};

    return postBackTornateElettorali(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackTornateElettoraliMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackTornateElettorali>>
>;
export type PostBackTornateElettoraliMutationBody = TornataElettoraleRequest;
export type PostBackTornateElettoraliMutationError = ErrorType<unknown>;

export const usePostBackTornateElettorali = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackTornateElettorali>>,
    TError,
    { data: TornataElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostBackTornateElettoraliMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Report Excel dei votanti di una tornata elettorale
 */
export const getBackTornateElettoraliIdVotatiXlsx = (
  idTornataElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/votati/xlsx`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliIdVotatiXlsxQueryKey = (
  idTornataElettorale: string,
) =>
  [
    `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/votati/xlsx`,
  ] as const;

export const getGetBackTornateElettoraliIdVotatiXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackTornateElettoraliIdVotatiXlsxQueryKey(idTornataElettorale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>
  > = ({ signal }) =>
    getBackTornateElettoraliIdVotatiXlsx(
      idTornataElettorale,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTornataElettorale, ...queryOptions };
};

export type GetBackTornateElettoraliIdVotatiXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>
>;
export type GetBackTornateElettoraliIdVotatiXlsxQueryError = ErrorType<unknown>;

export const useGetBackTornateElettoraliIdVotatiXlsx = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVotatiXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliIdVotatiXlsxQueryOptions(
    idTornataElettorale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Report Excel dei votanti di una tornata elettorale
 */
export const getBackTornateElettoraliIdVotantiXlsx = (
  idTornataElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/votanti/xlsx`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliIdVotantiXlsxQueryKey = (
  idTornataElettorale: string,
) =>
  [
    `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/votanti/xlsx`,
  ] as const;

export const getGetBackTornateElettoraliIdVotantiXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackTornateElettoraliIdVotantiXlsxQueryKey(idTornataElettorale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>
  > = ({ signal }) =>
    getBackTornateElettoraliIdVotantiXlsx(
      idTornataElettorale,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTornataElettorale, ...queryOptions };
};

export type GetBackTornateElettoraliIdVotantiXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>
>;
export type GetBackTornateElettoraliIdVotantiXlsxQueryError =
  ErrorType<unknown>;

export const useGetBackTornateElettoraliIdVotantiXlsx = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVotantiXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliIdVotantiXlsxQueryOptions(
    idTornataElettorale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Report Excel dei vincitori di una tornata elettorale
 */
export const getBackTornateElettoraliIdVincitoriXlsx = (
  idTornataElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/vincitori/xlsx`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliIdVincitoriXlsxQueryKey = (
  idTornataElettorale: string,
) =>
  [
    `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/vincitori/xlsx`,
  ] as const;

export const getGetBackTornateElettoraliIdVincitoriXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackTornateElettoraliIdVincitoriXlsxQueryKey(idTornataElettorale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>
  > = ({ signal }) =>
    getBackTornateElettoraliIdVincitoriXlsx(
      idTornataElettorale,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTornataElettorale, ...queryOptions };
};

export type GetBackTornateElettoraliIdVincitoriXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>
>;
export type GetBackTornateElettoraliIdVincitoriXlsxQueryError =
  ErrorType<unknown>;

export const useGetBackTornateElettoraliIdVincitoriXlsx = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdVincitoriXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliIdVincitoriXlsxQueryOptions(
    idTornataElettorale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Report Excel degli elettori di una tornata elettorale
 */
export const getBackTornateElettoraliIdElettoriXlsx = (
  idTornataElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/elettori/xlsx`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackTornateElettoraliIdElettoriXlsxQueryKey = (
  idTornataElettorale: string,
) =>
  [
    `/framework-bo-procedimenti/tornate-elettorali/${idTornataElettorale}/elettori/xlsx`,
  ] as const;

export const getGetBackTornateElettoraliIdElettoriXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackTornateElettoraliIdElettoriXlsxQueryKey(idTornataElettorale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>
  > = ({ signal }) =>
    getBackTornateElettoraliIdElettoriXlsx(
      idTornataElettorale,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTornataElettorale, ...queryOptions };
};

export type GetBackTornateElettoraliIdElettoriXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>
>;
export type GetBackTornateElettoraliIdElettoriXlsxQueryError =
  ErrorType<unknown>;

export const useGetBackTornateElettoraliIdElettoriXlsx = <
  TData = Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>,
  TError = ErrorType<unknown>,
>(
  idTornataElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackTornateElettoraliIdElettoriXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackTornateElettoraliIdElettoriXlsxQueryOptions(
    idTornataElettorale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
